import { makeJobRequest, postJob } from './JobsApi';

/**
 * Every concrete `JobDefinition` subclass's `jobTypeId` is a literal of this
 * type (e.g. `static jobTypeId = 'drugflow';`) — see `jobRegistry.js`'s
 * `JobTypes`, which derives the friendly enum other code imports from those
 * literals, rather than each subclass importing an enum declared here.
 * @typedef {'folding' | 'maplight' | 'gnina_scoring' | 'reinvent'
 *  | 'gnina_docking' | 'library_screen' | 'drugflow'} JobTypeOptions
 */

/**
 * @typedef {'structure' | 'compound' | 'screening' | 'workspace'} JobResultDestinationOptions
 * @type {{
 *    STRUCTURE: "structure",
 *    COMPOUND: "compound",
 *    SCREENING: "screening",
 *    WORKSPACE: "workspace"
 * }}
 */
export const JobResultDestinations = {
    STRUCTURE: 'structure',
    COMPOUND: 'compound',
    SCREENING: 'screening',
    WORKSPACE: 'workspace',
};

/**
 * @typedef {'succeeded' | 'running' | 'failed' | 'starting' | 'unknown'} JobStatusOptions
 * @type {{
 *    SUCCEEDED: "succeeded",
 *    RUNNING: "running",
 *    FAILED: "failed",
 *    STARTING: "starting",
 *    UNKNOWN: "unknown"
 * }}
 */
export const JobStatus = {
    SUCCEEDED: 'succeeded',
    RUNNING: 'running',
    FAILED: 'failed',
    STARTING: 'starting',
    UNKNOWN: 'unknown',
};

/**
 * @typedef {object} JobConstructorData
 * @property {string} [jobId] - The job id assigned by the job manager.
 * @property {string} [created] - ISO timestamp the job was created.
 * @property {string} [delivered] - ISO timestamp results were delivered.
 * @property {string} [done] - ISO timestamp the job finished.
 * @property {string} [modified] - ISO timestamp the job was last modified.
 * @property {object} [requestmeta] - Job-type specific request metadata.
 * @property {object} [stash] - Caller-supplied data round-tripped with the job.
 * @property {object} [jobrequest] - The original request payload.
 * @property {object} [results] - The job results packet, if available.
 */

/**
 * @typedef {object} IntegrateResultsReturn
 * @property {object} [results] - The (possibly transformed) results packet to
 *   pass back to the caller. Shape is job-type specific.
 * @property {string} [error] - A user-facing error string, if integration failed.
 */

/**
 * @typedef {object} IntegrateResultsOptions
 * @property {Array<import('BMapsModel').Compound|import('BMapsModel').MapCase>} [items]
 *   - The workspace items the results should be applied to, when applicable.
 * @property {object} [loadOptions] - Structure/compound load options.
 * @property {object} [dataConnection] - The data connection to load results into.
 */

/**
 * Metadata for a single column in a job results table,
 *   returned by {@link JobDefinition.getDataInfo}.
 *
 * @typedef {object} DataInfoEntry
 * @property {string} [label] - Human-readable column header shown in the table.
 * @property {string} [description] - Tooltip text shown on column header hover.
 * @property {'number' | 'string'} [type] - MUI column type. Controls filter UI and sort behavior.
 *   Defaults to 'number' if omitted. Set to 'string' for text-valued columns.
 * @property {string} [directionality] - One of the {@link Directionality} values. Controls the
 *   default sort direction (MAXIMIZE → descending first, MINIMIZE → ascending first) and the
 *   empty-value sort position. Omit for columns with no meaningful directionality.
 * @property {boolean} [defaultVisible] - If true, the column is visible by default.
 *   Hidden by default if omitted.
 * @property {boolean} [structureValue] - If true, the value is a SMILES string rendered as a
 *   molecule structure image. Sets type to 'string' and disables sorting and filtering.
 * @property {boolean} [excludeFromTable] - If true, the column is excluded.
 * @property {boolean} [excludeFromImport] - If true, the value isn't written onto the compound
 *   as a property when a results row is imported. Separate from `excludeFromTable`, since a
 *   hidden column can still be a real result worth importing.
 * @property {string} [propApplicability] - One of the {@link PropApplicability} values. Controls
 *   which entity (compound, pose, etc.) the property belongs to.
 * @property {string} [unit] - Unit label appended to the description tooltip.
 * @property {string} [range] - Expected value range shown in the description tooltip.
 * @property {string} [goodRange] - Ideal value range shown in the description tooltip.
 */

/**
 * Abstract base class for all job types managed by jobman.
 *
 * A concrete job type extends this class and:
 *  - sets the static descriptors (`jobTypeId`, `resultDestination`,
 *    `jobTypeName`, `jobName`, and optionally `propertyScope`, `idField`,
 *    `scopedPropertyMetadata`);
 *  - implements {@link JobDefinition#integrateResults} to apply the job's
 *    results to the workspace / redux state;
 *  - optionally overrides {@link JobDefinition#getRequestMeta} and the static
 *    {@link JobDefinition.getDataInfo} to customize the request envelope and
 *    the results-table column metadata.
 *
 * Instances are created by the jobs redux layer
 * (see `BMapsRedux/jobs/thunks` `loadResult`) via `new JobClass({ jobId })`.
 *
 * @abstract
 */
export class JobDefinition {
    /**
     * Stable job-type identifier; one of {@link JobTypes}. Also used as the
     * default property scope when `propertyScope` is not set.
     * @type {JobTypeOptions}
     */
    static jobTypeId = undefined;

    /**
     * Where this job's results are surfaced in the UI.
     * @type {JobResultDestinationOptions}
     */
    static resultDestination = undefined;

    /**
     * Scoped property metadata contributed by this job type. Aggregated by
     * `jobRegistry.getJobsPropertyMetadata` and registered on the workspace.
     * @type {object}
     */
    static scopedPropertyMetadata = {};

    /**
     * The result-row field used as the unique row id in results tables.
     * Consumers fall back to `'id'` when this is undefined.
     * @type {string|undefined}
     */
    static idField = undefined;

    /** Human-readable job type name, e.g. "Gnina Scoring". @type {string} */
    static jobTypeName = undefined;

    /** Human-readable job instance name, e.g. "Gnina Scoring Job". @type {string} */
    static jobName = undefined;

    /**
     * Short description of what the program takes, what it produces, and any caveats — a
     * continuation of `jobTypeName`, not a standalone sentence (e.g. "designs new molecules
     * within..." for a `jobTypeName` of "DrugFlow"): {@link getJobDescription} prepends the name
     * rather than each subclass repeating it. Omit to show no tooltip/description at all.
     * @type {string|undefined}
     */
    static jobDescription = undefined;

    /**
     * A citation URL for the underlying program, shown alongside {@link jobDescription}
     * wherever it's displayed. Omit to show no citation.
     * @type {string|undefined}
     */
    static jobCitation = undefined;

    /**
     * True if this job type needs a protein loaded in the workspace to run. The program picker
     * disables the option while none is loaded.
     * @type {boolean}
     */
    static requiresLoadedProtein = false;

    /**
     * The scope under which this job's properties are stored on a compound.
     * Defaults to `jobTypeId`. Override with a static getter to write into another
     * job type's scope (e.g. Gnina Docking → Gnina Scoring).
     *
     * This uses a getter rather than `= undefined` so the CLASS answers this the same way an
     * instance does. Consumers reach job types through `lookupJobDefinition`, which
     * returns the class, not an instance — resolving the default only in the
     * constructor left every such caller reading `undefined` for every job type but
     * Gnina Docking, and writing the job's properties to the compound's root group
     * instead of its scope. See BP2-2736.
     * @returns {string|undefined}
     */
    static get propertyScope() { return this.jobTypeId; }

    /**
     * Copies the static descriptors onto the instance and seeds instance
     * state from the supplied job data.
     * @param {JobConstructorData} data
     */
    constructor({
        jobId,
        created,
        delivered,
        done,
        modified,
        requestmeta,
        stash,
        jobrequest,
        results,
    }) {
        // Copy static properties from Job Class into the instance
        this.jobTypeId = this.constructor.jobTypeId;
        this.propertyScope = this.constructor.propertyScope;
        this.resultDestination = this.constructor.resultDestination;
        this.dataInfo = this.constructor.dataInfo;
        this.jobTypeName = this.constructor.jobTypeName;
        this.jobName = this.constructor.jobName;

        this.jobid = jobId;
        this.jobCreationError = undefined;

        this.updateFrom({
            created,
            delivered,
            done,
            modified,
            requestmeta,
            stash,
            jobrequest,
            results,
        });
    }

    /**
     * Merges a job data packet into the instance, mapping incoming server
     * keys to the instance's internal property names.
     * @param {Partial<JobConstructorData> & { jobid?: string, jobtype?: string,
     *   state?: import('./JobDefinition').JobStatusOptions }} [data]
     * @returns {this}
     */
    updateFrom(data = {}) {
        const keyMap = {
            jobid: 'jobid',
            jobtype: 'jobTypeId',
            created: 'created',
            delivered: 'delivered',
            done: 'done',
            modified: 'modified',
            requestmeta: 'requestMeta',
            stash: 'stash',
            jobrequest: 'jobRequest',
            results: 'results',
            state: 'state',
        };

        Object.entries(keyMap).forEach(([incomingKey, internalKey]) => {
            if (Object.prototype.hasOwnProperty.call(data, incomingKey)) {
                this[internalKey] = data[incomingKey];
            }
        });

        return this;
    }

    /** @returns {JobTypeOptions} Alias of `jobTypeId` for redux/job-state shape parity. */
    get jobtype() { return this.jobTypeId; }

    /**
     * Builds the results-table column metadata by flattening
     * `scopedPropertyMetadata`. Job types whose results-table columns differ
     * from their scoped properties (e.g. Gnina Docking) override this.
     * @returns {Object<string, DataInfoEntry>} map of property name → column metadata
     */
    static getDataInfo() {
        const result = {};
        const scopedMeta = this.scopedPropertyMetadata || {};
        for (const entityMeta of Object.values(scopedMeta)) {
            for (const group of Object.values(entityMeta.childGroups || {})) {
                for (const [key, prop] of Object.entries(group.properties || {})) {
                    result[key] = prop.metadata || {};
                }
            }
        }
        return result;
    }

    /** @returns {Object<string, object>} {@link JobDefinition.getDataInfo} as a getter. */
    static get dataInfo() { return this.getDataInfo(); }

    /** @returns {string} Human-readable job type name. */
    static getJobTypeName() { return this.jobTypeName; }

    /** @returns {string} Human-readable job instance name. */
    static getJobName() { return this.jobName; }

    /**
     * @returns {string|undefined} `jobTypeName` and `jobDescription` composed together — the
     *   description shown as the program picker option's tooltip and in the results tab.
     *   `undefined` (not just the bare name) when this job type has no `jobDescription`.
     */
    static getJobDescription() {
        return this.jobDescription ? `${this.jobTypeName} ${this.jobDescription}` : undefined;
    }

    /** @returns {string|undefined} Citation URL for the underlying program. */
    static getJobCitation() { return this.jobCitation; }

    /** @returns {object} This job type's scoped property metadata (or `{}`). */
    static getScopedPropertyMetadata() {
        return this.scopedPropertyMetadata || {};
    }

    /**
     * Wraps a request payload in the job-manager envelope, attaching the
     * request metadata produced by {@link JobDefinition#getRequestMeta}.
     * @param {object} jobrequest - The job-type specific request payload.
     * @param {object} [stash] - Caller data round-tripped with the job.
     * @returns {object} The job-manager request envelope.
     */
    prepareRequest(jobrequest, stash={}) {
        return makeJobRequest({
            jobtype: this.jobTypeId,
            jobrequest,
            stash,
            requestmeta: this.getRequestMeta(jobrequest, stash),
        });
    }

    /**
     * Produces the `requestmeta` stored alongside the job. Override to expose
     * additional job-type specific metadata (see e.g. FoldingJob).
     * @param {object} jobrequest - The job-type specific request payload.
     * @param {object} _stash - Caller data round-tripped with the job (unused
     *   in the base implementation).
     * @returns {object} request metadata
     */
    getRequestMeta(jobrequest, _stash) {
        return {
            jobName: jobrequest?.jobName,
        };
    }

    /**
     * Submits the job to the job manager and records the response on this
     * instance.
     * @param {object} jobrequest - The job-type specific request payload.
     * @param {object} [stash] - Caller data round-tripped with the job.
     * @returns {Promise<import('./JobsApi').postJobResponse>} the post-job response
     */
    async sendJobCreationRequest(jobrequest, stash={}) {
        const requestData = this.prepareRequest(jobrequest, stash);
        const response = await postJob(requestData);
        this.updatePostJobResponse(response);
        return response;
    }

    /**
     * Applies the result of {@link JobDefinition#sendJobCreationRequest} to
     * this instance, recording any creation error.
     * @param {{ results?: object, error?: string }} response
     * @returns {void}
     */
    updatePostJobResponse(response) {
        const { results, error } = response;
        if (results) {
            this.updateFrom(results);
        }
        this.jobCreationError = error;
    }

    /**
     * Applies a completed job's results to the application: loading
     * structures, setting compound properties, updating local job state, etc.
     *
     * This is the primary extension point of a job type and MUST be
     * implemented by every concrete subclass.
     *
     * @abstract
     * @param {object} _job - The redux job-state record for this job.
     * @param {object|string} _results - The results packet from the job
     *   manager (may be a JSON string that the implementation parses).
     * @param {IntegrateResultsOptions} [_options]
     * @returns {Promise<IntegrateResultsReturn>}
     */
    async integrateResults(_job, _results, _options) {
        throw new Error(
            `${this.constructor.name}.integrateResults is not implemented`
        );
    }
}
